import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { UploadCloud, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, FileText } from "lucide-react";

function StatTile({ tone, label, value, icon: Icon, testid }) {
  const toneClass =
    tone === "emerald" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
    tone === "rose"    ? "text-rose-700 bg-rose-50 border-rose-200" :
    tone === "amber"   ? "text-amber-700 bg-amber-50 border-amber-200" :
    "text-foreground bg-secondary/30 border-border";
  return (
    <div className={`border ${toneClass} p-5`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="meta-label">{label}</div>
        <Icon size={16} />
      </div>
      <div className="font-display text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}

function SampleList({ title, rows, testid, tone }) {
  if (!rows || rows.length === 0) return null;
  const toneClass = tone === "rose" ? "border-rose-200" : tone === "amber" ? "border-amber-200" : "border-border";
  return (
    <div className={`border ${toneClass} p-4`} data-testid={testid}>
      <div className="meta-label mb-2">{title} (first {rows.length})</div>
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li key={i} className="text-xs font-mono">
            <span className="text-muted-foreground">Row {r.row}:</span>{" "}
            <span className="">{r.work_email || "(no email)"}</span>{" "}
            <span className="text-muted-foreground">— {r.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BulkUpload() {
  const inputRef = useRef(null);
  const { options } = useAuth();
  const [file, setFile] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [skipZb, setSkipZb] = useState(false);
  const [skipMasterExcl, setSkipMasterExcl] = useState(false);
  const [skipCampaignExcl, setSkipCampaignExcl] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [headerMismatch, setHeaderMismatch] = useState(null);

  // Trusted Upload visibility — admin or per-user trusted_bulk_upload grant.
  const canSkipZb = options?.trusted_bulk_upload === true;

  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get("/leads/bulk/template", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "xlsx" ? "lead_template.xlsx" : "lead_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadReport = async (fmt) => {
    if (!result?.report_id) return;
    try {
      const res = await api.get(`/leads/bulk/report/${result.report_id}`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const base = (result.filename || "bulk").replace(/\.(csv|xlsx)$/i, "");
      a.download = `${base}_validation_report.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onUpload = async () => {
    setError(""); setResult(null); setHeaderMismatch(null);
    if (!file) return setError("Choose a CSV or XLSX file first.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (campaignName) fd.append("campaign_name", campaignName);
      if (skipZb && canSkipZb) fd.append("skip_zerobounce", "1");
      if (skipMasterExcl) fd.append("skip_master_exclusion", "1");
      if (skipCampaignExcl) fd.append("skip_campaign_exclusion", "1");
      const { data } = await api.post("/leads/bulk/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      // Auto-download a rejected-rows XLSX so the agent doesn't have to click
      // a second button. Skips the auto-download when there were no rejections.
      const rejectedCount = (data.duplicates || 0) + (data.invalid_emails || 0)
        + (data.invalid_phones || 0) + (data.invalid_websites || 0) + (data.errors || 0);
      if (rejectedCount > 0 && data.report_id) {
        try {
          const res = await api.get(`/leads/bulk/report/${data.report_id}`, {
            params: { fmt: "xlsx", rejected_only: 1 },
            responseType: "blob",
          });
          const url = URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement("a");
          a.href = url;
          const base = (data.filename || "bulk").replace(/\.(csv|xlsx)$/i, "");
          a.download = `${base}_rejected.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        } catch { /* don't block the upload success path */ }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Strict header validation: backend returns a structured 400 with
      // {code:'header_mismatch'|'missing_required_columns', ...details}.
      // We render an actionable inline panel instead of a flat toast.
      if (
        detail && typeof detail === "object" &&
        (detail.code === "header_mismatch" || detail.code === "missing_required_columns")
      ) {
        setHeaderMismatch(detail);
      } else {
        setError(formatApiErrorDetail(detail) || e.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Action"
        title="Bulk Upload"
        description="Upload many leads at once via CSV or Excel. Same field schema as the form. ZeroBounce validates each work email and duplicates are blocked automatically."
        testid="bulk-upload-header"
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="download-template-csv" onClick={() => downloadTemplate("csv")} variant="outline" className="rounded-sm h-10">
              <Download size={14} className="mr-1" /> CSV Template
            </Button>
            <Button data-testid="download-template-xlsx" onClick={() => downloadTemplate("xlsx")} variant="outline" className="rounded-sm h-10">
              <Download size={14} className="mr-1" /> XLSX Template
            </Button>
          </div>
        }
      />
      <div className="p-8 space-y-6 max-w-5xl">
        {/* Optional campaign override — if picked, the backend stamps
            every row in the uploaded file with this campaign_name,
            overriding whatever value is in the row's CSV column.
            Leave blank to use the per-row value from the file. */}
        <div className="border border-border bg-secondary/20 p-4 rounded-sm" data-testid="bulk-campaign-card">
          <label className="meta-label mb-2 block" htmlFor="bulk-campaign-select">
            Campaign (optional · applies to every row in this batch)
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              id="bulk-campaign-select"
              data-testid="bulk-campaign-select"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="h-10 px-3 border border-input bg-background text-sm rounded-sm min-w-[280px]"
            >
              <option value="">— Use campaign_name from CSV —</option>
              {(options?.campaigns ?? []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {campaignName && (
              <span className="text-[11px] px-2 py-1 rounded-sm badge-info" data-testid="bulk-campaign-badge">
                Every row will be stamped: <span className="font-mono ml-1">{campaignName}</span>
              </span>
            )}
            {campaignName && (
              <button
                type="button"
                onClick={() => setCampaignName("")}
                className="text-[11px] text-muted-foreground hover:underline"
                data-testid="bulk-campaign-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {canSkipZb && (
          <div className="mb-6 border border-amber-200 bg-amber-50 p-4 rounded-sm" data-testid="trusted-upload-block">
            <label className="inline-flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="bulk-skip-zerobounce"
                checked={skipZb}
                onChange={(e) => setSkipZb(e.target.checked)}
                className="mt-1 rounded-sm"
              />
              <div className="text-sm">
                <div className="font-medium text-amber-900">Skip ZeroBounce validation (trusted upload)</div>
                <div className="text-xs text-amber-800/80 mt-0.5">
                  Saves ZeroBounce credits. Enable ONLY for files where you&apos;ve already
                  validated the email addresses externally. Format checks are also skipped —
                  duplicate checks against existing leads + Master Exclusion still run.
                </div>
              </div>
            </label>
          </div>
        )}

        <div className="mb-6 border border-zinc-200 bg-zinc-50 p-4 rounded-sm space-y-3" data-testid="exclusion-skip-block">
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
            Exclusion list mirroring — on-demand override
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="bulk-skip-master-exclusion"
              checked={skipMasterExcl}
              onChange={(e) => setSkipMasterExcl(e.target.checked)}
              className="mt-1 rounded-sm"
            />
            <div className="text-sm">
              <div className="font-medium">Skip adding emails to Master Exclusion</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                By default, every accepted lead is mirrored into the Master Exclusion list
                so the same email can&apos;t be re-uploaded later. Tick this to skip that
                mirror for this upload only (e.g. a temporary import you don&apos;t want
                to permanently suppress).
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="bulk-skip-campaign-exclusion"
              checked={skipCampaignExcl}
              onChange={(e) => setSkipCampaignExcl(e.target.checked)}
              className="mt-1 rounded-sm"
            />
            <div className="text-sm">
              <div className="font-medium">Skip adding emails to Campaign Exclusion</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Skip mirroring accepted leads into this campaign&apos;s exclusion list. Tick
                when you want the leads to still be eligible for future outreach within the
                same campaign.
              </div>
            </div>
          </label>
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          className="border border-dashed border-zinc-300 bg-zinc-50 hover:bg-zinc-100 transition-colors p-12 cursor-pointer text-center rounded-sm"
          data-testid="bulk-dropzone"
        >
          <UploadCloud size={28} className="mx-auto text-primary mb-3" />
          <div className="font-display text-xl font-bold">Click to select a .csv or .xlsx file</div>
          <div className="text-sm text-muted-foreground mt-1">Up to ~1,000 leads per upload (recommended). Each work email is checked against the database (duplicates) and ZeroBounce.</div>
          {file && (
            <div className="mt-4 text-sm font-mono flex items-center justify-center gap-2">
              <FileSpreadsheet size={14} /> {file.name} · {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
          <input
            ref={inputRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="bulk-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="bulk-upload-button" onClick={onUpload} disabled={loading || !file} className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
            {loading ? "Validating…" : "Upload & Validate"}
          </Button>
          {file && (
            <Button variant="outline" className="rounded-sm h-10" onClick={() => { setFile(null); setResult(null); setError(""); }} data-testid="bulk-clear">
              Clear
            </Button>
          )}
        </div>

        {error && <div data-testid="bulk-error" className="badge-error px-4 py-3 rounded-sm text-sm">{error}</div>}

        {headerMismatch && (
          <div
            data-testid="bulk-header-mismatch"
            className="border-2 border-rose-300 bg-rose-50 text-rose-900 rounded-sm p-4 space-y-3 mb-4"
          >
            <div className="flex items-start gap-2">
              <div className="text-rose-700 font-display text-base font-semibold">
                ⚠ Upload blocked — {headerMismatch.code === "missing_required_columns"
                  ? "required columns are missing"
                  : "column headers don't match"}
              </div>
            </div>
            <p className="text-sm">{headerMismatch.message}</p>

            {headerMismatch.code === "header_mismatch" && (
              <div>
                <div className="meta-label text-rose-800 mb-1">Headers we couldn&apos;t recognise</div>
                <div className="flex flex-wrap gap-1.5">
                  {(headerMismatch.unknown_columns || []).map((c) => (
                    <span
                      key={c}
                      data-testid={`bad-header-${c}`}
                      className="text-[11px] font-mono px-2 py-1 rounded-sm bg-rose-100 border border-rose-300 text-rose-900"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {headerMismatch.code === "missing_required_columns" && (
              <div>
                <div className="meta-label text-rose-800 mb-1">Missing required columns</div>
                <div className="flex flex-wrap gap-1.5">
                  {(headerMismatch.missing_columns || []).map((c) => (
                    <span
                      key={c}
                      data-testid={`missing-col-${c}`}
                      className="text-[11px] font-mono px-2 py-1 rounded-sm bg-rose-100 border border-rose-300 text-rose-900"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {headerMismatch.code === "header_mismatch" && (
              <div>
                <div className="meta-label text-rose-800 mb-1">Common aliases that DO work</div>
                <ul className="text-[12px] font-mono text-rose-900/90 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                  {(headerMismatch.accepted_aliases_examples || []).map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadTemplate("xlsx")}
                data-testid="bulk-mismatch-download-template"
                className="rounded-sm border-rose-300 hover:bg-rose-100 text-rose-900"
              >
                <Download size={12} className="mr-1" /> Download canonical template
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setHeaderMismatch(null); setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                data-testid="bulk-mismatch-dismiss"
                className="rounded-sm text-rose-900 hover:bg-rose-100"
              >
                Pick a new file
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="bulk-result">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatTile tone="emerald" testid="bulk-stat-inserted"  label="Inserted"         value={result.inserted} icon={CheckCircle2} />
              <StatTile tone="rose"    testid="bulk-stat-duplicates" label="Duplicates"      value={result.duplicates} icon={Copy} />
              <StatTile tone="rose"    testid="bulk-stat-invalid"   label="Invalid Emails"   value={result.invalid_emails} icon={AlertTriangle} />
              <StatTile tone="rose"    testid="bulk-stat-invalid-phones"   label="Invalid Phones"   value={result.invalid_phones ?? 0} icon={AlertTriangle} />
              <StatTile tone="rose"    testid="bulk-stat-invalid-websites" label="Invalid Websites" value={result.invalid_websites ?? 0} icon={AlertTriangle} />
              <StatTile tone="amber"   testid="bulk-stat-errors"    label="Other Errors"     value={result.errors} icon={FileText} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">
                Processed {result.total_rows} row{result.total_rows === 1 ? "" : "s"} from <span className="font-mono">{result.filename}</span>.
              </span>
              {result.skipped_zerobounce && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 font-mono" data-testid="zb-skipped-chip">
                  ZeroBounce: skipped (trusted upload)
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button data-testid="download-report-xlsx" onClick={() => downloadReport("xlsx")} className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
                  <Download size={14} className="mr-1" /> Download Validation Report (.xlsx)
                </Button>
                <Button data-testid="download-report-csv" onClick={() => downloadReport("csv")} variant="outline" className="rounded-sm h-10">
                  <Download size={14} className="mr-1" /> .csv
                </Button>
              </div>
            </div>

            {result.unknown_columns?.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 px-4 py-3 rounded-sm" data-testid="bulk-unknown-cols">
                <div className="meta-label mb-1 text-amber-700">Ignored columns (not in template)</div>
                <div className="font-mono text-xs">{result.unknown_columns.join(", ")}</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <SampleList testid="sample-duplicates"        tone="rose"  title="Duplicates"        rows={result.sample?.duplicates} />
              <SampleList testid="sample-invalid-emails"    tone="rose"  title="Invalid Emails"    rows={result.sample?.invalid_emails} />
              <SampleList testid="sample-invalid-phones"    tone="rose"  title="Invalid Phones"    rows={result.sample?.invalid_phones} />
              <SampleList testid="sample-invalid-websites"  tone="rose"  title="Invalid Websites"  rows={result.sample?.invalid_websites} />
              <SampleList testid="sample-errors"            tone="amber" title="Other Errors"      rows={result.sample?.errors} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
